/**
 * D20/E6/RN42 — Sun rejects the whole workflow save when two or more jobs of the SAME workflow
 * instance carry the same normalized planning group value
 * (`StandardWorkflowService.checkPlanningGroupsBeforeSave`). The rejection travels as HTTP 409
 * CONFLICT (`rest/Utils/Utils.java:67`), with the frozen, untranslated English literal repeated
 * once per duplicated value:
 *
 *   "Planning group '<value>' is assigned to more than one job in this workflow. A planning
 *   group can only be used once per fabrication order."
 *
 * Both save routes of the Workflow 2.0 editor — templates (`routes/MainWorkflows.jsx`, via
 * `Workflow.jsx`'s own save) and the fabrication order's own save
 * (`configuration/orders/FabricationOrder.jsx`) — must recognise this shape and show the user a
 * message built from Jupiter's OWN i18n key (S65), never Sun's raw English literal, while still
 * letting the user identify the duplicated value(s) (S63/S64).
 */

const DUPLICATE_PLANNING_GROUP_LITERAL = /Planning group '([^']*)' is assigned to more than one job in this workflow\./g

/**
 * @param {{status?: number, message?: string}} error Error thrown by `Api_Functions` on a
 *   non-2xx response.
 * @returns {string[]} The duplicated planning group value(s) named in Sun's E6 literal, in the
 *   order they appear. Empty when `error` is not an E6 rejection.
 */
export const extractDuplicatePlanningGroupValues = (error) => {
  const message = typeof error?.message === 'string' ? error.message : ''
  const pattern = new RegExp(DUPLICATE_PLANNING_GROUP_LITERAL)
  const values = []
  let match = pattern.exec(message)
  while (match !== null) {
    values.push(match[1])
    match = pattern.exec(message)
  }
  return values
}

/**
 * @param {{status?: number, message?: string}} error Error thrown by `Api_Functions`.
 * @returns {boolean} True when the workflow save was rejected because of a duplicated planning
 *   group within the same workflow instance (E6/RN42/V9).
 */
export const isDuplicatePlanningGroupSaveError = (error) =>
  error?.status === 409 && extractDuplicatePlanningGroupValues(error).length > 0

/**
 * i18n key for the duplicated-group message shown in both save routes (S63/S64/S65). Deliberately
 * NOT Sun's literal: it is Jupiter's own key, translated in the 8 files of `src/components/local/`,
 * interpolating the offending value(s) extracted above.
 */
export const DUPLICATE_PLANNING_GROUP_MESSAGE_KEY = 'Planning group {{values}} is used on more than one job of this workflow. Use it only once per fabrication order.'
